import { useState } from 'react';

import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import { IInfoIcon } from '@/assets/icons/info-icons/i-info';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import DropdownActions from '@/components/table/dropdownActions';
import { MobileRow, MobileRowTitle, MobileRowValue, MobileTableWrapper, NoResults } from '@/components/table/mobileTable/mobileTable';
import { useAuth } from '@/hooks/use-auth';
import { dateFormatter, getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import { blinkingFrame } from '../../table/styles';
import { columns } from '../constants';
import { ModalWindowType } from '../modals/types';
import NextStep from './cell/nextStep';
import { AllocationWithUpdated } from './table';
import { getLastUpdateTimeStamp } from './tableBody';

type MobileTableProps = {
  recommendationsRows: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'] | [];
  currentRow: AllocationWithUpdated | null;
  openModalWindow: (type: ModalWindowType, row: AllocationWithUpdated) => void;
  actionsByStatus: (selectedItemName: string, row: AllocationWithUpdated) => void;
  openPage: (path: string) => void;
};

const MobileTable = ({ recommendationsRows, currentRow, openModalWindow, actionsByStatus, openPage }: MobileTableProps) => {
  const [selectedRow, setSelectedRow] = useState<string>();

  const TableRow = ({ el }: { el: AllocationWithUpdated }) => {
    const { user } = useAuth();

    return (
      <MobileTableRowWrap key={el.id} loading={!!el.updated ? 1 : 0} onClick={() => setSelectedRow(el.id)} isSelected={el.id === selectedRow}>
        {user && user.tenant.type === TenantType.PLATFORM_MANAGER && (
          <MobileRow>
            <MobileRowTitle>{columns[0].title}</MobileRowTitle>
            <MobileRowValue>{el.tenant.name}</MobileRowValue>
          </MobileRow>
        )}

        <MobileRow>
          <MobileRowTitle>{columns[1].title}</MobileRowTitle>
          <MobileRowValue
            isRedirect
            onClick={() => openPage(`/investors/${el.legalEntity?.family.id}/${el.legalEntity?.id}?backToTitle=Transactions`)}
          >
            {el.legalEntity.entityName}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[2].title}</MobileRowTitle>
          <MobileRowValue isRedirect onClick={() => openPage(`/strategyDetails/${el.investment.id}?backToTitle=Transactions`)}>
            {el.investment.name}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[3].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{el.committedCapital}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[4].title}</MobileRowTitle>
          <MobileRowValue>{el?.investment?.investorDocsDue && getUTCDate(new Date(el.investment.investorDocsDue))}</MobileRowValue>
        </MobileRow>

        {/* <MobileRow>
          <MobileRowTitle>{columns[5].title}</MobileRowTitle>
          <MobileRowValue>
            {el.assignee && `${el.assignee.firstName && el.assignee?.firstName} ${el.assignee?.lastName && el.assignee.lastName}`}
          </MobileRowValue>
        </MobileRow> */}

        <MobileRow>
          <MobileRowTitle>{columns[5].title}</MobileRowTitle>
          <LastUpdateMobileRowValue isRedirect>
            <div onClick={() => openPage(`transactionDetails/${el.id}?backToTitle=Transactions`)}>
              <Tooltip title={getLastUpdateTimeStamp(new Date(el.lastUpdatedAt))}>
                {dateFormatter(format(new Date(el.lastUpdatedAt), 'yyyy-MM-dd'), 'yyyy-MM-dd')}
              </Tooltip>
            </div>
            {!el.decision && el.reason === 'Always Show' && (
              <Tooltip title="Show All">
                <IInfoIcon style={{ display: 'flex', alignItems: 'center' }} fill={themeVar('context.warning')} />
              </Tooltip>
            )}
          </LastUpdateMobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{columns[6].title}</MobileRowTitle>
          <MobileRowValue>
            <NextStepWrapper>
              <NextStep status={el.operationsWorkflowTransitionTask.name} />
            </NextStepWrapper>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowValue>
            {!!el?.operationsWorkflowTransitionTask?.availableActions?.length && (
              <DropdownActions
                isMobile
                isListCenter
                selected={selectedRow === el.id}
                items={el?.operationsWorkflowTransitionTask?.availableActions?.map((action) => ({
                  name: _.startCase(action),
                  onClick: () => actionsByStatus(action, el)
                }))}
                disabled={!!el.updated}
              />
            )}
          </MobileRowValue>
        </MobileRow>
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {recommendationsRows.length ? (
        recommendationsRows.map((el: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number], index) => {
          return <TableRow el={el} key={el.id + index} />;
        })
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default MobileTable;

const LastUpdateMobileRowValue = styled(MobileRowValue)`
  gap: 5px;
`;

const MobileTableRowWrap = styled.div<{ zIndex?: number; loading?: boolean | number; isSelected?: boolean }>`
  //z-index: 30;
  width: 100%;
  padding: 14px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${({ isSelected }) => (isSelected ? themeVar('layer.12') : themeVar('layer.1'))};
  border-radius: 8px;
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  animation: ${({ loading }) => (loading ? blinkingFrame : null)} 1.5s infinite;
  ${({ zIndex }) =>
    zIndex &&
    css`
      z-index: ${zIndex};
    `}
`;

const NextStepWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;
